import React, { useState } from 'react';

import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import Icon from '../../../components/AppIcon';

const IssueDescriptionForm = ({ 
  description, 
  onDescriptionChange,
  severity,
  onSeverityChange,
  affectedServices,
  onAffectedServicesChange,
  errors,
  className = '' 
}) => {
  const [charCount, setCharCount] = useState(description?.length || 0);
  const maxChars = 1000;

  const severityLevels = [
    { 
      value: 'minor', 
      label: 'Minor Issue',
      description: 'Slight degradation, service mostly functional'
    },
    { 
      value: 'moderate', 
      label: 'Moderate Issue',
      description: 'Noticeable problems, some features affected'
    },
    { 
      value: 'major', 
      label: 'Major Outage',
      description: 'Significant service disruption'
    },
    { 
      value: 'critical', 
      label: 'Critical Outage',
      description: 'Complete service failure'
    }
  ];

  const serviceTypes = [
    { id: 'web', label: 'Website/Web App', icon: 'Globe' },
    { id: 'mobile', label: 'Mobile App', icon: 'Smartphone' },
    { id: 'api', label: 'API/Backend', icon: 'Server' },
    { id: 'email', label: 'Email Services', icon: 'Mail' },
    { id: 'sms', label: 'SMS/Messaging', icon: 'MessageSquare' },
    { id: 'voice', label: 'Voice/Calls', icon: 'Phone' },
    { id: 'payment', label: 'Payment Processing', icon: 'CreditCard' },
    { id: 'authentication', label: 'Login/Authentication', icon: 'Lock' }
  ];

  const handleDescriptionChange = (e) => {
    const value = e?.target?.value;
    if (value?.length <= maxChars) {
      setCharCount(value?.length);
      onDescriptionChange(value);
    }
  };

  const handleServiceToggle = (serviceId, checked) => {
    if (checked) {
      onAffectedServicesChange([...affectedServices, serviceId]);
    } else {
      onAffectedServicesChange(affectedServices?.filter(id => id !== serviceId));
    }
  };

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Issue Description */}
      <div className="space-y-4">
        <div className="flex items-center space-x-2">
          <Icon name="FileText" size={20} className="text-primary" />
          <h3 className="text-lg font-semibold text-foreground">Issue Description</h3>
        </div>
        
        <div className="space-y-2">
          <textarea
            className="w-full min-h-32 p-3 border border-border rounded-md bg-input text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent resize-y"
            placeholder="Describe the issue you're experiencing in detail. Include what you were trying to do, what happened, and any error messages you received..."
            value={description || ''}
            onChange={handleDescriptionChange}
            maxLength={maxChars}
          />
          
          <div className="flex justify-between items-center text-sm">
            <span className={`${errors?.description ? 'text-error' : 'text-muted-foreground'}`}>
              {errors?.description || 'Provide detailed information to help others understand the issue'}
            </span>
            <span className={`${charCount > maxChars * 0.9 ? 'text-warning' : 'text-muted-foreground'}`}>
              {charCount}/{maxChars}
            </span>
          </div>
        </div>
      </div>
      {/* Severity Level */}
      <div className="space-y-4">
        <div className="flex items-center space-x-2">
          <Icon name="AlertTriangle" size={20} className="text-primary" />
          <h3 className="text-lg font-semibold text-foreground">Severity Level</h3>
        </div>
        
        <Select
          label="Impact Severity"
          description="How severely is the service affected?"
          placeholder="Select severity level..."
          options={severityLevels}
          value={severity}
          onChange={onSeverityChange}
          error={errors?.severity}
          required
          className="w-full"
        />
      </div>
      {/* Affected Services */}
      <div className="space-y-4">
        <div className="flex items-center space-x-2">
          <Icon name="Settings" size={20} className="text-primary" />
          <h3 className="text-lg font-semibold text-foreground">Affected Services</h3>
        </div>
        
        <div className="space-y-2">
          <p className="text-sm text-muted-foreground">
            Select all service components that are experiencing issues
          </p>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {serviceTypes?.map((service) => (
              <div key={service?.id} className="flex items-center space-x-3 p-3 border border-border rounded-md hover:bg-muted/50 transition-colors">
                <Checkbox
                  checked={affectedServices?.includes(service?.id)}
                  onChange={(e) => handleServiceToggle(service?.id, e?.target?.checked)}
                />
                <Icon name={service?.icon} size={16} className="text-muted-foreground" />
                <span className="text-sm font-medium text-foreground">{service?.label}</span>
              </div>
            ))}
          </div>
          
          {errors?.affectedServices && (
            <div className="flex items-center space-x-2 p-3 bg-error/10 border border-error/20 rounded-md">
              <Icon name="AlertCircle" size={16} className="text-error" />
              <span className="text-sm text-error">{errors?.affectedServices}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default IssueDescriptionForm;